import { z } from 'zod';
/**
 * CRM sync (M3.12, ADR-028, `INTEGRATIONS.md` §9A, FR-CRM-01 to FR-CRM-06).
 *
 * Three nouns, and the boundary between them is the whole of the HIPAA argument:
 *
 * | Shape | Authority | Stored in |
 * |---|---|---|
 * | `CrmMapping` | the clinic admin | `integration_installs.settings.crmMapping` |
 * | `CrmLink` | the sync job | `crm_contact_links` |
 * | `CrmSyncRun` | the sync job | `crm_sync_runs` |
 *
 * ## Why the mapping is a choice *within* an allow-list, never a free mapping
 *
 * FR-CRM-06 gives the clinic the field mapping, and a literal reading of that - "type a
 * platform field name, type a HighLevel field name" - is a text box through which a
 * clinic can put a diagnosis into a marketing system. So what the admin actually picks,
 * per CRM custom field, is **one of the platform's four source fields, or `none`**
 * (`CRM_SOURCE_FIELDS`). The set of destinations is fixed by
 * `@platform/integrations-sdk`'s `CRM_CUSTOM_FIELD_KEYS`, the set of sources is fixed
 * here, and the scrubber in the SDK refuses anything else at the door. A mapping UI
 * built on these shapes cannot express a disclosure.
 *
 * ## Tags are marketing, and marketing needs an authorization
 *
 * `tags` are per platform event, and they are applied **only** to a patient with an
 * active `marketing_authorization` consent (45 CFR 164.508(a)(3)). A patient without
 * one gets the operational fields or nothing at all - see `operationalSyncEnabled` -
 * and the withdrawal path strips the tags again and mirrors DND on the contact.
 *
 * Tag text is validated here for *grammar* (lower case, no free prose) and again by the
 * SDK scrubber for *vocabulary* at the moment of the PUT, so a clinic cannot save
 * `allergy-shot-recall` and discover a year later that it named a condition.
 */
/* --------------------------------------------------------------------- events ---- */
/**
 * The platform events a CRM sync reacts to.
 *
 * Deliberately the appointment lifecycle and nothing else. A CRM's legitimate questions
 * are "who booked", "who is due back" and "who did not show"; every one of those is
 * answered by a status transition, and none of them needs to know why the visit was
 * booked.
 */
export const CRM_SYNC_EVENTS = [
    'booked',
    'rescheduled',
    'cancelled',
    'completed',
    'no_show',
];
export const CrmSyncEventSchema = z.enum(CRM_SYNC_EVENTS);
/* -------------------------------------------------------------------- mapping ---- */
/**
 * The CRM custom fields a mapping may fill.
 *
 * A transcription of `CRM_CUSTOM_FIELD_KEYS` in `@platform/integrations-sdk`, because
 * `@platform/contracts` has no runtime dependency on the SDK (it is imported by the
 * browser apps, which have no business pulling in an adapter kit). The two are asserted
 * equal by `apps/api/test/crm/crm-units.spec.ts`, so the copy cannot drift.
 */
export const CRM_MAPPED_FIELDS = [
    'appointment_type_key',
    'next_appointment_date',
    'last_appointment_status',
    'booking_source',
];
export const CrmMappedFieldSchema = z.enum(CRM_MAPPED_FIELDS);
/**
 * The platform values a CRM field may be filled from. **This is the allow-list.**
 *
 * Each one is a fact that is either identical for thousands of patients (the visit type
 * key, the booking channel) or a bare date and status of the patient's *own* next visit.
 * None of them is a clinical finding about this patient, and there is no entry that
 * could carry one. `none` is how an admin switches a field off without the platform
 * having to invent an "enabled" flag beside every row.
 */
export const CRM_SOURCE_FIELDS = [
    'none',
    'appointment_type_key',
    'next_appointment_at',
    'appointment_status',
    'booking_source',
];
export const CrmSourceFieldSchema = z.enum(CRM_SOURCE_FIELDS);
/**
 * A marketing tag.
 *
 * Lower case, digits, `-`, `_` and `:`. No spaces and no capitals, which is what stops a
 * tag being a sentence: `type:allergy_consult` is a tag, `Patient said her eczema is
 * worse` is not expressible. The 40-character ceiling is the same argument from the
 * other end.
 */
export const CrmTagSchema = z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9][a-z0-9:_-]*$/, 'Use lower-case letters, digits, "-", "_" or ":".');
/** Up to eight tags per event: a campaign filter, not a note field. */
const CrmTagListSchema = z.array(CrmTagSchema).max(8).default([]);
export const CrmTagsByEventSchema = z
    .object({
    booked: CrmTagListSchema,
    rescheduled: CrmTagListSchema,
    cancelled: CrmTagListSchema,
    completed: CrmTagListSchema,
    no_show: CrmTagListSchema,
})
    .strict();
export const CrmFieldMapSchema = z
    .object({
    appointment_type_key: CrmSourceFieldSchema.default('appointment_type_key'),
    next_appointment_date: CrmSourceFieldSchema.default('next_appointment_at'),
    last_appointment_status: CrmSourceFieldSchema.default('appointment_status'),
    booking_source: CrmSourceFieldSchema.default('booking_source'),
})
    .strict();
/**
 * Everything the clinic admin decides about the CRM sync.
 *
 * ## `operationalSyncEnabled` defaults to **false**
 *
 * A patient who has not signed a marketing authorization has still had their *identity*
 * and the fact that they are this clinic's patient disclosed to HighLevel the moment a
 * contact exists. That is permitted under the BAA as a business-associate disclosure,
 * and it is still a disclosure - so it is opt-in, per tenant, with the clinic saying so.
 * Off is the state in which nothing about a non-consenting patient leaves the platform.
 *
 * ## `hipaaModeConfirmed` is an attestation, not a checkbox
 *
 * HighLevel is not HIPAA compliant by default: the add-on is bought at agency level and
 * the mode is switched on **per sub-account**. Only the clinic can see that switch, so
 * only the clinic can attest to it, and until it does the sync refuses every field that
 * identifies a patient. Setting it records the instant in the install's
 * `hipaaModeConfirmedAt` setting (`INTEGRATIONS.md` §9A "BAA status").
 */
export const CrmMappingSchema = z
    .object({
    tags: CrmTagsByEventSchema,
    customFields: CrmFieldMapSchema,
    operationalSyncEnabled: z.boolean().default(false),
    hipaaModeConfirmed: z.boolean().default(false),
})
    .strict();
/** `PUT /admin/integrations/highlevel/mapping`. Every field is replaced, not merged. */
export const UpdateCrmMappingRequestSchema = CrmMappingSchema;
/**
 * `GET /admin/integrations/highlevel/mapping`.
 *
 * `installed` and `enabled` are here so the settings screen can render the whole state
 * without a second call, and `baaSignedAt` because the two attestations are read
 * together and refusing to sync without either is one sentence to a user, not two.
 */
export const CrmMappingResponseSchema = z
    .object({
    data: z
        .object({
        mapping: CrmMappingSchema,
        installed: z.boolean(),
        enabled: z.boolean(),
        baaSignedAt: z.iso.datetime().nullable().default(null),
        hipaaModeConfirmedAt: z.iso.datetime().nullable().default(null),
        /** Source fields the UI may offer, so a new one does not need a UI release. */
        sourceFields: z.array(CrmSourceFieldSchema),
        mappedFields: z.array(CrmMappedFieldSchema),
    })
        .strict(),
})
    .strict();
/* ----------------------------------------------------------------------- link ---- */
export const CRM_LINK_STATUSES = ['linked', 'pending', 'failed', 'unlinked'];
export const CrmLinkStatusSchema = z.enum(CRM_LINK_STATUSES);
/**
 * One patient's mapping into one CRM (`crm_contact_links`).
 *
 * `lastSyncedHash` is what makes a resync idempotent: the job hashes the payload it is
 * about to send and does nothing when the hash is unchanged. `lastError` is an error
 * **class**, never a vendor sentence, because a vendor sentence can quote the field that
 * upset it and that field is a patient's.
 */
export const CrmLinkSchema = z
    .object({
    patientId: z.uuid(),
    provider: z.string().min(1).max(64),
    externalId: z.string().max(200).nullable().default(null),
    status: CrmLinkStatusSchema,
    lastSyncedHash: z.string().max(64).nullable().default(null),
    lastSyncedAt: z.iso.datetime().nullable().default(null),
    lastError: z.string().max(120).nullable().default(null),
    lastErrorAt: z.iso.datetime().nullable().default(null),
    marketingSyncedAt: z.iso.datetime().nullable().default(null),
})
    .strict();
export const CRM_SYNC_RUN_OUTCOMES = [
    'synced',
    'unchanged',
    'skipped_no_install',
    'skipped_no_consent',
    'skipped_not_attested',
    'failed',
    // Booking-funnel leads (ADR-037).
    'skipped_leads_off',
    'skipped_existing_patient',
    'skipped_no_lead',
];
export const CrmSyncRunOutcomeSchema = z.enum(CRM_SYNC_RUN_OUTCOMES);
/** One attempt, as the patient's CRM panel shows it (`crm_sync_runs`). */
export const CrmSyncRunSchema = z
    .object({
    id: z.uuid(),
    kind: z.string().min(1).max(40),
    trigger: z.string().min(1).max(40),
    outcome: CrmSyncRunOutcomeSchema,
    /** `full` when marketing fields travelled, `minimal` when only the operational set. */
    mode: z.enum(['full', 'minimal', 'none']),
    errorClass: z.string().max(120).nullable().default(null),
    startedAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable().default(null),
})
    .strict();
/** `GET /admin/patients/:id/crm`. */
export const CrmPatientStatusResponseSchema = z
    .object({
    data: z
        .object({
        link: CrmLinkSchema.nullable().default(null),
        /** Whether a marketing authorization is on file **and** still in force. */
        marketingAuthorized: z.boolean(),
        runs: z.array(CrmSyncRunSchema),
    })
        .strict(),
})
    .strict();
/* -------------------------------------------------------------------- actions ---- */
/**
 * `POST /admin/patients/:id/crm/resync` and `POST /admin/integrations/highlevel/sync-now`.
 *
 * Both answer "what did you queue", not "what happened": the work is a job, and a route
 * that waited for a vendor would be a route that times out when the vendor does.
 */
export const CrmResyncResponseSchema = z
    .object({
    data: z
        .object({
        queued: z.boolean(),
        /** Why nothing was queued, when `queued` is false. Never a vendor message. */
        reason: z.string().max(120).nullable().default(null),
    })
        .strict(),
})
    .strict();
export const CrmSyncNowRequestSchema = z
    .object({
    /**
     * Resume a backfill. Opaque, and it is a patient id: the walk is ordered by id, so
     * the cursor is the last one handled rather than an offset that shifts under a
     * concurrent insert.
     */
    cursor: z.uuid().nullable().default(null),
    /** Patients per job. Bounded because each one is a vendor call under a rate limit. */
    pageSize: z.number().int().min(1).max(100).default(50),
})
    .strict();
export const CrmSyncNowResponseSchema = z
    .object({
    data: z
        .object({
        queued: z.boolean(),
        pageSize: z.number().int(),
        cursor: z.uuid().nullable().default(null),
        reason: z.string().max(120).nullable().default(null),
    })
        .strict(),
})
    .strict();
/**
 * The default mapping, and the reason each default is what it is.
 *
 * Every custom field is mapped to its natural source, because a CRM whose recall
 * campaign has no "next appointment" is a CRM nobody uses; every tag list is **empty**,
 * because a tag the clinic did not choose is a tag the clinic cannot explain; and both
 * switches are **off**, because attesting on a clinic's behalf is the one thing this
 * platform must never do.
 */
export const DEFAULT_CRM_MAPPING = Object.freeze({
    tags: { booked: [], rescheduled: [], cancelled: [], completed: [], no_show: [] },
    customFields: {
        appointment_type_key: 'appointment_type_key',
        next_appointment_date: 'next_appointment_at',
        last_appointment_status: 'appointment_status',
        booking_source: 'booking_source',
    },
    operationalSyncEnabled: false,
    hipaaModeConfirmed: false,
});
//# sourceMappingURL=crm.js.map